/**
 * Native date manipulation utilities to replace moment.js operations
 * Provides functions for adding/subtracting time and getting start/end of periods
 */

/**
 * Add days to a date
 */
export const addDays = (date: Date, days: number): Date => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

/**
 * Subtract days from a date
 */
export const subtractDays = (date: Date, days: number): Date => {
  return addDays(date, -days);
};

/**
 * Add months to a date
 */
export const addMonths = (date: Date, months: number): Date => {
  const result = new Date(date);
  result.setMonth(result.getMonth() + months);
  return result;
};

/**
 * Subtract months from a date
 */
export const subtractMonths = (date: Date, months: number): Date => {
  return addMonths(date, -months);
};

/**
 * Add years to a date
 */
export const addYears = (date: Date, years: number): Date => {
  const result = new Date(date);
  result.setFullYear(result.getFullYear() + years);
  return result;
};

/**
 * Subtract years from a date
 */
export const subtractYears = (date: Date, years: number): Date => {
  return addYears(date, -years);
};

/**
 * Get the first day of the week for a given locale
 * Returns 0 for Sunday, 1 for Monday, etc.
 */
export const getFirstDayFromLocale = (locale: string = 'en-US'): number => {
  try {
    const loc = new Intl.Locale(locale) as unknown as {
      getWeekInfo?: () => { firstDay: number };
      weekInfo?: { firstDay: number };
    };
    const info = loc.getWeekInfo ? loc.getWeekInfo() : loc.weekInfo;
    if (info?.firstDay) {
      // Intl.Locale returns 1-7 (Monday-Sunday), we need 0-6 (Sunday-Saturday)
      return info.firstDay % 7;
    }
  } catch {
    // Default to Monday for most locales
    return 1;
  }
  return 1; // Default to Monday
};

/**
 * Get the start of the week for a given date
 * Uses ISO week (Monday as first day) by default
 */
export const getStartOfISOWeek = (date: Date): Date => {
  const result = new Date(date);
  const day = result.getDay();
  const diff = (day === 0 ? -6 : 1) - day; // Monday is first day
  result.setDate(result.getDate() + diff);
  result.setHours(0, 0, 0, 0);
  return result;
};

/**
 * Get the end of the week for a given date
 * Uses ISO week (Sunday as last day)
 */
export const getEndOfISOWeek = (date: Date): Date => {
  const result = getStartOfISOWeek(date);
  result.setDate(result.getDate() + 6);
  result.setHours(23, 59, 59, 999);
  return result;
};

/**
 * Get the start of the week for a given date based on locale
 */
export const getStartOfWeek = (date: Date, firstDayOfWeek: number = 1): Date => {
  const result = new Date(date);
  const day = result.getDay();
  const diff = (day - firstDayOfWeek + 7) % 7;
  result.setDate(result.getDate() - diff);
  result.setHours(0, 0, 0, 0);
  return result;
};

/**
 * Get the end of the week for a given date based on locale
 */
export const getEndOfWeek = (date: Date, firstDayOfWeek: number = 1): Date => {
  const startOfWeek = getStartOfWeek(date, firstDayOfWeek);
  const result = new Date(startOfWeek);
  result.setDate(result.getDate() + 6);
  result.setHours(23, 59, 59, 999);
  return result;
};

/**
 * Get the start of the month for a given date
 */
export const getStartOfMonth = (date: Date): Date => {
  const result = new Date(date);
  result.setDate(1);
  result.setHours(0, 0, 0, 0);
  return result;
};

/**
 * Get the end of the month for a given date
 */
export const getEndOfMonth = (date: Date): Date => {
  const result = new Date(date);
  result.setMonth(result.getMonth() + 1);
  result.setDate(0);
  result.setHours(23, 59, 59, 999);
  return result;
};

/**
 * Get the start of the year for a given date
 */
export const getStartOfYear = (date: Date): Date => {
  const result = new Date(date);
  result.setMonth(0);
  result.setDate(1);
  result.setHours(0, 0, 0, 0);
  return result;
};

/**
 * Get the end of the year for a given date
 */
export const getEndOfYear = (date: Date): Date => {
  const result = new Date(date);
  result.setMonth(11);
  result.setDate(31);
  result.setHours(23, 59, 59, 999);
  return result;
};

/**
 * Get the start of the day for a given date
 */
export const getStartOfDay = (date: Date): Date => {
  const result = new Date(date);
  result.setHours(0, 0, 0, 0);
  return result;
};

/**
 * Get the end of the day for a given date
 */
export const getEndOfDay = (date: Date): Date => {
  const result = new Date(date);
  result.setHours(23, 59, 59, 999);
  return result;
};

/**
 * Convert a Date to Unix timestamp (seconds)
 */
export const toUnixTimestamp = (date: Date): number => {
  return Math.floor(date.getTime() / 1000);
};

/**
 * Convert Unix timestamp (seconds) to Date
 */
export const fromUnixTimestamp = (timestamp: number): Date => {
  return new Date(timestamp * 1000);
};
